import React, { useCallback, useMemo, useState } from 'react';

import {
  useRecordContext,
  useResourceContext,
} from 'react-admin';

import { DateTime } from 'luxon';
import { Grid } from '@mui/material';

import SubNavigation from '../layout/SubNavigation';
import { StyledTabs, StyledTab } from '../layout/Tab';

import { ValueOf } from '../../utilities/types';

import Params from './Params';

import DailySum from './tabs/DailySum';
import RollingSum from './tabs/RollingSum';
import DetailedView from './tabs/DetailedView';
import Flow from './tabs/flow';
import Distribution from './tabs/Distribution';
import { requestTypes } from '../../constants/requestTypes';
import TransactionFilterForm from './TransactionFilterForm';
import entityTypes, { entityTypesFromResource } from '../../constants/entityTypes';
import { IIndividual } from '../../utilities/schemas/individual';
import { IBusiness } from '../../utilities/schemas/business';
import { IProduct } from '../../utilities/schemas/product';
import { IAccountEntity } from '../../utilities/schemas/accountEntity';
import supportsTransactionFlow from '../../utilities/supportsTransactionFlow';
import toStockholmTime from '../../utilities/toStockholmTime';

const tabs = {
  DAILY_SUM: 'dailySum',
  ROLLING_SUM: 'rollingSum',
  DETAILED_VIEW: 'detailedView',
  FLOW: 'flow',
  DISTRIBUTION: 'distribution',
} as const;

type TabValues = ValueOf<typeof tabs>;

const Transaction = () => {
  const [tab, setTab] = useState<TabValues>(tabs.DAILY_SUM);

  const resource = useResourceContext();
  const record = useRecordContext<IIndividual | IBusiness | IProduct | IAccountEntity>();
  const entityType = resource ? entityTypesFromResource[resource] : entityTypes.INDIVIDUAL;

  const currentDate = DateTime.now();

  const [params, setParams] = useState<Params>({
    from: currentDate.minus({ years: 1 }).toISODate()!,
    to: currentDate.toISODate()!,
    currency: record?.currencies?.at(0) ?? 'EUR',
    entityId: record?.id || '',
    entityType: resource ? entityTypesFromResource[resource] : entityTypes.INDIVIDUAL,
    rollingSumWindow: 30,
    distributionWindow: 0,
    maxDepth: 1,
    selectedRow: undefined,
    flowPageSize: 10,
  });

  const shouldDisplayFlowDistribution = supportsTransactionFlow(params.entityType);
  const isHiddenTabSelected = !shouldDisplayFlowDistribution
    && (tab === tabs.FLOW || tab === tabs.DISTRIBUTION);
  const activeTab = isHiddenTabSelected ? tabs.DAILY_SUM : tab;

  const filter = useMemo(() => ({
    entityId: params.entityId,
    entityType: params.entityType,
    requestType: requestTypes.TRANSACTION,
    from: toStockholmTime(params.from).toISODate()!,
    currency: params.currency,
    to: toStockholmTime(params.to).toISODate()!,
    transactionTypeConfiguration: params.selectedRow,
  }), [params]);

  const handleTabChange = useCallback((
    _event: React.SyntheticEvent,
    newValue: ValueOf<typeof tabs>,
  ) => {
    setTab(newValue);
  }, []);

  if (!record) return null;

  return (
    <Grid container spacing={6}>
      <Grid size={12}>
        <TransactionFilterForm
          setParams={setParams}
          params={params}
          filter={filter}
          entityType={entityType}
        />
      </Grid>
      <Grid size={12} container spacing={4}>
        <Grid size={12}>
          <SubNavigation>
            <StyledTabs value={activeTab} onChange={handleTabChange}>
              <StyledTab label="Daily sum" value={tabs.DAILY_SUM} />
              <StyledTab label="Rolling sum" value={tabs.ROLLING_SUM} />
              { shouldDisplayFlowDistribution && <StyledTab label="Flow" value={tabs.FLOW} /> }
              { shouldDisplayFlowDistribution && <StyledTab label="Distribution" value={tabs.DISTRIBUTION} /> }
              <StyledTab label="Detailed view" value={tabs.DETAILED_VIEW} />
            </StyledTabs>
          </SubNavigation>
        </Grid>
        <Grid size={12}>
          {activeTab === tabs.DAILY_SUM
            && <DailySum filter={filter} />}
          {activeTab === tabs.ROLLING_SUM
            && <RollingSum filter={filter} setParams={setParams} params={params} />}
          {activeTab === tabs.FLOW
            && <Flow setParams={setParams} params={params} />}
          {activeTab === tabs.DISTRIBUTION
            && <Distribution filter={filter} setParams={setParams} params={params} />}
          {activeTab === tabs.DETAILED_VIEW
            && <DetailedView filter={filter} />}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default Transaction;
